/**
 * Core type definitions for RTC adapter
 * @packageDocumentation
 */

/**
 * Connection state for RTC providers.
 * Represents the current state of the RTC connection.
 */
export enum ConnectionState {
  /** Not connected to any RTC server */
  Disconnected = 'disconnected',
  /** Currently connecting to RTC server */
  Connecting = 'connecting',
  /** Successfully connected to RTC server */
  Connected = 'connected',
  /** Reconnecting after connection loss */
  Reconnecting = 'reconnecting',
  /** Connection failed permanently */
  Failed = 'failed',
}

/**
 * Stream statistics for monitoring and debugging.
 * Contains frame counts and timing information.
 * @internal
 */
export interface StreamStats {
  /** Frames received per second */
  framesPerSec: number;
  /** Total frames received since connection */
  totalFrames: number;
  /** Frames successfully sent (for publisher) */
  framesSent: number;
  /** Frames lost in transmission */
  framesLost: number;
  /** Frames recovered via error correction */
  framesRecovered: number;
  /** Frames dropped due to buffer overflow */
  framesDropped: number;
  /** Frames received out of sequence order */
  framesOutOfOrder: number;
  /** Duplicate frames received */
  framesDuplicate: number;
  /** Sequence number of last rendered frame */
  lastRenderedSeq: number;
}

/**
 * Animation frame metadata.
 * Contains information about each received animation frame.
 * @internal
 */
export interface AnimationFrameMetadata {
  /** Frame sequence number for ordering */
  frameSeq?: number;
  /** True if this is the first frame of a speaking session */
  isStart?: boolean;
  /** True if this is the last frame of a speaking session */
  isEnd?: boolean;
  /** True if this is an idle state frame */
  isIdle?: boolean;
  /** True if this frame was recovered via ALR */
  isRecovered?: boolean;
}

/**
 * LiveKit connection configuration.
 * Used when connecting via LiveKitProvider.
 */
export interface LiveKitConnectionConfig {
  /** LiveKit server URL (e.g., wss://your-server.livekit.cloud) */
  url: string;

  /** Authentication token */
  token: string;

  /** Room name to join */
  roomName: string;
}

/**
 * LiveKit connection prewarm configuration.
 * Used for URL-only or token-assisted connection preparation.
 */
export interface LiveKitPrepareConnectionConfig {
  /** LiveKit server URL (e.g., wss://your-server.livekit.cloud) */
  url: string;

  /** Authentication token, if available */
  token?: string;

  /** Optional room name for callers that already have the full config */
  roomName?: string;
}

/**
 * Agora connection configuration.
 * Used when connecting via AgoraProvider.
 */
export interface AgoraConnectionConfig {
  /** Agora Application ID (from Agora Console) */
  appId: string;

  /** Channel name to join */
  channel: string;

  /** Authentication token (optional for testing, required for production) */
  token?: string;

  /** User ID (optional, 0 or undefined = auto-assign) */
  uid?: number;
}

/**
 * RTC connection configuration.
 * Union type supporting both LiveKit and Agora providers.
 *
 * @example
 * ```typescript
 * // LiveKit
 * await player.connect({
 *   url: 'wss://your-server.livekit.cloud',
 *   token: 'your-token',
 *   roomName: 'my-room',
 * });
 *
 * // Agora
 * await player.connect({
 *   appId: 'your-agora-app-id',
 *   channel: 'my-channel',
 *   token: 'your-agora-token', // optional
 * });
 * ```
 */
export type RTCConnectionConfig =
  | LiveKitConnectionConfig
  | AgoraConnectionConfig;

/**
 * Connection preparation configuration.
 * Currently only LiveKit supports explicit prewarming.
 */
export type RTCPrepareConnectionConfig = LiveKitPrepareConnectionConfig;

/**
 * Type guard to check if config is for LiveKit.
 */
export function isLiveKitConfig(
  config: RTCConnectionConfig,
): config is LiveKitConnectionConfig {
  return 'url' in config && 'token' in config;
}

/**
 * Type guard to check if config can be used to prewarm a LiveKit connection.
 */
export function isLiveKitPrepareConfig(
  config: RTCPrepareConnectionConfig,
): config is LiveKitPrepareConnectionConfig {
  return 'url' in config;
}

/**
 * Type guard to check if config is for Agora.
 */
export function isAgoraConfig(
  config: RTCConnectionConfig,
): config is AgoraConnectionConfig {
  return 'appId' in config && 'channel' in config;
}
